import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * O CARTÃO DA CONVERSA — o que a lista mostra além da própria conversa.
 *
 * Douglas, 17/09/2026, ao reprovar o primeiro desenho: "estas coisas não é ali,
 * isso é no painel". Ele estava certo e a medição mostrou o tamanho do engano:
 * só **6%** das conversas ativas têm pedido na esteira. Valor, produto e agenda
 * na lista deixariam 94 de cada 100 cards com espaço vazio.
 *
 * Ficou o que responde "qual conversa eu abro agora":
 *   empresa      65% das ativas — é o que tapa o buraco do número cru
 *   nível do dono 65% — a Lei dos 3 Donos, com os ícones que o CRM já usa
 *   fila         a do item mais atrasado, em uma palavra
 *
 * ⚠️ A empresa vem do CNPJ, NÃO de `destinatario_id`: aquele campo está
 * preenchido em **6 conversas de 5.291**, e a lista já tinha um join por ele que
 * devolvia nulo em 99,9% das vezes.
 *
 * Custo medido: 10,6 ms para as 200 que a lista carrega, tudo em index scan.
 * Em fatias de 150 ids — `.in()` com 200 uuids estoura a URL do gateway.
 */

export type CartaoConversa = {
  cliente_id: string | null;
  razao_social: string | null;
  tipo_pessoa: string | null;
  /** DONO_CARTEIRA · DONO_NEGOCIO · DONO_LEAD — null quando não há cadastro. */
  nivel_dono: string | null;
  quadro_atual: string | null;
  coluna_atual: string | null;
  tipo_lead: string | null;
  /** Status do lead — a lista mostra ISTO quando ainda não há pedido. */
  status_lead: string | null;
};

export function useConversaCartao(conversationIds: string[]) {
  const ids = Array.from(new Set(conversationIds.filter(Boolean))).sort();

  return useQuery({
    queryKey: ['conversa-cartao', ids],
    enabled: ids.length > 0,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    queryFn: async (): Promise<Map<string, CartaoConversa>> => {
      const mapa = new Map<string, CartaoConversa>();
      try {
        for (let i = 0; i < ids.length; i += 150) {
          const fatia = ids.slice(i, i + 150);
          const { data, error } = await supabase
            .from('v_conversa_cartao')
            .select('conversation_id, cliente_id, razao_social, tipo_pessoa, nivel_dono, quadro_atual, coluna_atual, tipo_lead, status_lead')
            .in('conversation_id', fatia);
          if (error) throw error;
          for (const r of (data || []) as Array<Record<string, unknown>>) {
            const id = r.conversation_id as string | null;
            if (!id) continue;
            mapa.set(id, {
              cliente_id: (r.cliente_id as string | null) ?? null,
              razao_social: (r.razao_social as string | null) ?? null,
              tipo_pessoa: (r.tipo_pessoa as string | null) ?? null,
              nivel_dono: (r.nivel_dono as string | null) ?? null,
              quadro_atual: (r.quadro_atual as string | null) ?? null,
              coluna_atual: (r.coluna_atual as string | null) ?? null,
              tipo_lead: (r.tipo_lead as string | null) ?? null,
              status_lead: (r.status_lead as string | null) ?? null,
            });
          }
        }
      } catch {
        // Lista sem cartão continua sendo a lista — nunca derruba a tela.
      }
      return mapa;
    },
  });
}

/**
 * AS CONVERSAS QUE CASAM com um recorte de PEDIDO (quadro · status · tipo de
 * lead · documento).
 *
 * Existe porque esses quatro não estão em `whatsapp_conversations`: dependem de
 * juntar cliente e pedido. A consulta da lista não faz esse join — e não deve
 * fazer, porque ela roda a cada digitação na busca.
 *
 * Então o caminho é o inverso: aqui se pergunta QUAIS conversas casam, e a
 * lista filtra por id.
 *
 * ⚠️ TEM TETO, e o teto é honesto: acima de `TETO_IDS` a URL do gateway não
 * aguenta a lista de uuids. Quando estourar, devolve `estourou: true` e a tela
 * avisa em vez de mostrar um recorte pela metade fingindo ser o todo — que é
 * exatamente o defeito que a lista já teve com as 200.
 */
export const TETO_IDS = 300;

export function useConversasPorPedido(filtro: {
  quadro?: string[];
  status?: string[];
  tipoLead?: string[];
  documento?: string[];
  statusLead?: string[];
}) {
  const quadro = (filtro.quadro ?? []).slice().sort();
  const status = (filtro.status ?? []).slice().sort();
  const tipoLead = (filtro.tipoLead ?? []).slice().sort();
  const documento = (filtro.documento ?? []).slice().sort();
  const statusLead = (filtro.statusLead ?? []).slice().sort();
  const ligado = quadro.length + status.length + tipoLead.length + documento.length + statusLead.length > 0;

  return useQuery({
    queryKey: ['conversas-por-pedido', quadro, status, tipoLead, documento, statusLead],
    enabled: ligado,
    staleTime: 60_000,
    queryFn: async (): Promise<{ ids: string[]; estourou: boolean }> => {
      /* ⛔ QUADRO e STATUS vêm de `v_conversa_filas`, que tem UMA LINHA POR FILA
         (Douglas 17/09/2026): "eu tenho dois pedidos, um em PEDIDO FECHADO
         outro em INSPEÇÃO... quando eu filtrar INSPEÇÃO ele vai aparecer;
         quando filtrar PEDIDO FECHADO ele também aparece."
         Pela fila principal — que é o que a lista EXIBE — filtrar PEDIDO
         FECHADO não acharia esse cliente. São 288 conversas hoje com o cliente
         em mais de uma fila, o recorde em 3.
         A lista continua com UM card por conversa: o que muda é a pergunta do
         filtro — "tem ALGUM pedido nesta fila?", não "a fila principal é esta?". */
      const ids = new Set<string>();
      let estourou = false;

      if (quadro.length || status.length) {
        let qf = supabase.from('v_conversa_filas').select('conversation_id').limit(TETO_IDS * 3 + 1);
        if (quadro.length) qf = qf.in('quadro_atual', quadro);
        if (status.length) qf = qf.in('coluna_atual', status);
        const { data, error } = await qf;
        if (error) return { ids: [], estourou: false };
        for (const r of (data || [])) {
          const id = (r as { conversation_id: string | null }).conversation_id;
          if (id) ids.add(id);
        }
      }

      /* Tipo de lead e documento são da CONVERSA, não da fila — saem do cartão.
         Quando os dois grupos estão ligados, o resultado é a INTERSEÇÃO: cruzar
         entre grupos é a regra do filtro, e somar aqui devolveria conversas que
         só casam com metade do que foi pedido. */
      if (tipoLead.length || documento.length || statusLead.length) {
        let qc = supabase.from('v_conversa_cartao').select('conversation_id').limit(TETO_IDS * 3 + 1);
        if (tipoLead.length) qc = qc.in('tipo_lead', tipoLead);
        if (documento.length) qc = qc.in('tipo_pessoa', documento);
        if (statusLead.length) qc = qc.in('status_lead', statusLead);
        const { data, error } = await qc;
        if (error) return { ids: [], estourou: false };
        const doCartao = new Set(
          (data || [])
            .map((r) => (r as { conversation_id: string | null }).conversation_id)
            .filter((x): x is string => !!x),
        );
        if (quadro.length || status.length) {
          for (const id of Array.from(ids)) if (!doCartao.has(id)) ids.delete(id);
        } else {
          for (const id of doCartao) ids.add(id);
        }
      }

      const todos = Array.from(ids);
      estourou = todos.length > TETO_IDS;
      return { ids: todos.slice(0, TETO_IDS), estourou };
    },
  });
}
